// src/App.jsx
import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import Navbar from "./components/Navbar.jsx";
import Home from "./pages/Home.jsx";
import Pricing from "./pages/Pricing.jsx";
import Search from "./pages/Search.jsx";
import TiktokSearch from "./pages/TiktokSearch.jsx";
import Login from "./pages/Login.jsx";
import Signup from "./pages/Signup.jsx";
import Profile from "./pages/Profile.jsx";
import VerifyEmail from "./pages/VerifyEmail.jsx";
import SetPassword from "./pages/SetPassword.jsx";
import VerifyCode from "./pages/VerifyCode.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import ProtectedSearchRoute from "./components/ProtectedSearchRoute.jsx";
import RedirectIfAuth from "./components/RedirectIfAuth.jsx";
import Campaign from "./pages/Campaign.jsx";
import CampaignSteps from "./pages/CampaignSteps.jsx";
import Mailbox from "./pages/Mailbox.jsx";
import HelpWidget from "./components/HelpWidget.jsx";
import Privacy from "./pages/Privacy.jsx";
import Terms from "./pages/Terms.jsx";
import Contact from "./pages/Contact.jsx";
import AdminFreeCustomers from "./pages/AdminFreeCustomers.jsx";
import NotFound from "./pages/NotFound.jsx";
import BlogPillar from "./pages/BlogPillar.jsx";
import BlogIndex from "./pages/BlogIndex.jsx";
import AdminBlogEditor from "./pages/AdminBlogEditor.jsx";
import AppShell from "./components/AppShell.jsx";
import SmartLink from "./pages/SmartLink.jsx";
import AdsDashboard from "./pages/AdsDashboard.jsx";

// ---------- Plan helpers ----------
const ACTIVE_SUB_STATUSES = ["active", "trialing", "past_due"];
const OUTREACH_PLANS = ["outreach"]; // Outreach plan

function hasOutreachPlan(subscription) {
  if (!subscription) return false;
  const status = (subscription.status || "").toLowerCase();
  const name = (subscription.name || "").toLowerCase();
  return ACTIVE_SUB_STATUSES.includes(status) && OUTREACH_PLANS.includes(name);
}

// Free users can access everything now
function hasFreeAccess(user) {
  return !!user; // Any logged-in user has free access to all features
}

// ---------- Route-level plan guard for feature-gated routes ----------
function RequirePlan({ level, children }) {
  const { user, subscription, loading } = useAuth();
  const location = useLocation();

  if (loading) return null; // or a spinner

  // Must be logged in first
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    );
  }

  // Free users now have access to all features
  return children;
}

// ---------- Outer app: provides auth context ----------
export default function App() {
  return (
    <AuthProvider>
      <AppFrame />
    </AuthProvider>
  );
}

// ---------- Inner app: knows about user/subscription + routing ----------
function AppFrame() {
  const location = useLocation();
  const { user, subscription, loading } = useAuth();

  const authRoutes = [
    "/login",
    "/signup",
    "/verify-email",
    "/set-password",
    "/verify-code",
    "/forgot-password",
    "/reset-password",
  ];

  const searchParams = new URLSearchParams(location.search);
  const pricingLocked =
    location.pathname === "/pricing" &&
    searchParams.get("locked") === "1";

  // 🔹 All routes that should use the ChatGPT-style sidebar layout
  const sidebarRoutes = ["/search", "/campaign", "/profile"];

  // handle nested routes like /campaign/123/steps as well
  const isSidebarRoute = sidebarRoutes.some((base) =>
    location.pathname.startsWith(base)
  );

  const hideChrome =
    authRoutes.includes(location.pathname) ||
    pricingLocked ||
    isSidebarRoute;

  // ----- GLOBAL SUBSCRIPTION GATE -----
  // Free users now have access to all features, no subscription gate needed

  return (
    <>
      {!hideChrome && <Navbar />}

      <Routes>
        <Route
          path="/admin/blog-editor"
          element={
            <ProtectedRoute>
              <AdminBlogEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <RedirectIfAuth to="/profile">
              <Home />
            </RedirectIfAuth>
          }
        />

        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPillar />} />

        {/* Campaign features - now available to all users */}
        <Route
          path="/campaign"
          element={
            <ProtectedRoute>
              <AppShell>
              <Campaign />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/campaign/:id/steps"
          element={
            <ProtectedRoute>
              <AppShell>
              <CampaignSteps />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/mail"
          element={
            <ProtectedRoute>
              <Mailbox />
            </ProtectedRoute>
          }
        />

        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        <Route
          path="/login"
          element={
            <RedirectIfAuth to="/profile">
              <Login />
            </RedirectIfAuth>
          }
        />

        <Route
          path="/signup"
          element={
            <RedirectIfAuth to="/profile">
              <Signup />
            </RedirectIfAuth>
          }
        />

        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route path="/set-password" element={<SetPassword />} />

        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/adsdashboard" element={<AdsDashboard />} />
        <Route path="/smart-link/:slug" element={<SmartLink />} />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppShell>
              <Profile />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Free users + Basic-or-better: Search page */}
        <Route
          path="/search"
          element={
            <ProtectedSearchRoute>
              <AppShell>
              <Search />
              </AppShell>
            </ProtectedSearchRoute>
          }
        />
        <Route
          path="/tiktoksearch"
          element={
            <ProtectedSearchRoute>
              <TiktokSearch />
            </ProtectedSearchRoute>
          }
        />

        <Route
          path="/admin/free-customers"
          element={
            <ProtectedRoute>
              <AdminFreeCustomers />
            </ProtectedRoute>
          }
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {!hideChrome && <HelpWidget />}
    </>
  );
}